import {
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  UpdateWorkflowRequest,
  UpdateWorkflowResponse,
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  GetSpacesResponse,
  GetWorkflowExecutionRequest,
  GetWorkflowExecutionResponse,
  GetWorkflowsRequest,
  GetWorkflowsResponse,
  GetWorkflowRequest,
  GetWorkflowResponse,
  GetWorkflowExecutionsForSpaceRequest,
  GetWorkflowExecutionsResponse,
  GetCapabilitiesResponse,
  UpdateCapabilityRequest,
  UpdateCapabilityResponse,
  GetWorkflowTemplatesResponse,
  ProvideUserInputForWorkflowExecutionRequest,
  RetryWorkflowExecutionRequest,
  Notification,
  GetNotificationsRequest,
  GetNotificationsResponse,
  MarkNotificationsAsSeenRequest,
  GetPnlsRequest,
  GetPnlsResponse,
  GetAgentExecutionFrameSummariesResponse,
} from '@amzn/finspace-service-typescript-client';
import { Api } from './Api';
import { GetAllDimensionsReportingRequest, GetAllDimensionsReportingResponse } from './filters/transformers/request/ProductLineRequestTransformer';

/**
 * Path generators for FinSpaceService API endpoints
 */
export const PATH = {
  createWorkflow: (spaceId: string) => `/space/${spaceId}/workflow`,
  updateWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}`,
  getAllDimensions: '/fae/allDimensions',
  getSpaces: '/spaces',
  getWorkflows: (spaceId: string) => `/space/${spaceId}/workflows`,
  getWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}`,
  getWorkflowTemplates: `/workflow-templates`,
  getWorkflowExecutions: (spaceId: string) => `/space/${spaceId}/workflow-executions`,
  getWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) => `/space/${spaceId}/workflow/${workflowId}/execution/${workflowExecutionId}`,
  getCapabilities: '/capabilities',
  updateCapability: (capabilityId: string) => `/capability/${capabilityId}`,
  deleteWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}`,
  deleteWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) => 
    `/space/${spaceId}/workflow/${workflowId}/execution/${workflowExecutionId}`,
  executeWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}/execute`,
  provideUserInput: (spaceId: string, workflowId: string, workflowExecutionId: string) =>
    `/space/${spaceId}/workflow/${workflowId}/execution/${workflowExecutionId}/provide-user-input`,
  retryWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) =>
    `/space/${spaceId}/workflow/${workflowId}/execution/${workflowExecutionId}/retry`,
  getNotifications: '/notifications',
  markNotificationsAsSeen: '/notifications/mark-as-seen',
  getPnls: '/pnl',
  getAgentExecutionFrameSummaries: (agentSessionId: string) => `/agent/session/${agentSessionId}/execution-frame-summaries`,
};

/**
 * Create instance of API for FinSpaceService
 */
const API = new Api('FinSpaceService');

/**
 * This object implements all methods presents in FinSpaceService
 */
export const FinSpaceServiceApi = {
  createWorkflow(request: CreateWorkflowRequest): Promise<CreateWorkflowResponse> {
    return API.post(PATH.createWorkflow(request.spaceId!), request);
  },

  updateWorkflow(spaceId: string, workflowId: string, request: UpdateWorkflowRequest): Promise<UpdateWorkflowResponse> {
    return API.put(PATH.updateWorkflow(spaceId, workflowId), request);
  },

  getAllDimensions(request: GetAllDimensionsReportingRequest): Promise<GetAllDimensionsReportingResponse> {
    return API.post(PATH.getAllDimensions, request);
  },

  getSpaces(): Promise<GetSpacesResponse> {
    return API.get(PATH.getSpaces);
  },

  getWorkflows(request: GetWorkflowsRequest): Promise<GetWorkflowsResponse> {
    return API.get(PATH.getWorkflows(request.spaceId!));
  },

  getWorkflow(request: GetWorkflowRequest): Promise<GetWorkflowResponse> {
    return API.get(PATH.getWorkflow(request.spaceId!, request.workflowId!));
  },

  getCapabilities(): Promise<GetCapabilitiesResponse> {
    return API.get(PATH.getCapabilities);
  },

  updateCapability(capabilityId: string, request: UpdateCapabilityRequest): Promise<UpdateCapabilityResponse> {
    return API.put(PATH.updateCapability(capabilityId), request);
  },

  getWorkflowTemplates(): Promise<GetWorkflowTemplatesResponse> {
    return API.get(PATH.getWorkflowTemplates);
  },

  getAllWorkflowExecutions(request: GetWorkflowExecutionsForSpaceRequest): Promise<GetWorkflowExecutionsResponse> {
    return API.get(PATH.getWorkflowExecutions(request.spaceId!));
  },

  getWorkflowExecution(request: GetWorkflowExecutionRequest): Promise<GetWorkflowExecutionResponse> {
    return API.get(PATH.getWorkflowExecution(request.spaceId!, request.workflowId!, request.workflowExecutionId!));
  },

  deleteWorkflow(spaceId: string, workflowId: string): Promise<void> {
    return API.delete(PATH.deleteWorkflow(spaceId, workflowId));
  },

  deleteWorkflowExecution(spaceId: string, workflowId: string, workflowExecutionId: string): Promise<void> {
    return API.delete(PATH.deleteWorkflowExecution(spaceId, workflowId, workflowExecutionId));
  },

  executeWorkflow(request: ExecuteWorkflowRequest): Promise<ExecuteWorkflowResponse> {
    return API.post(PATH.executeWorkflow(request.spaceId!, request.workflowId!), request);
  },

  provideUserInput(request: ProvideUserInputForWorkflowExecutionRequest): Promise<void> {
    return API.post(PATH.provideUserInput(request.spaceId!, request.workflowId!, request.workflowExecutionId!), request);
  },

  retryWorkflowExecution(request: RetryWorkflowExecutionRequest): Promise<void> {
    return API.post(PATH.retryWorkflowExecution(request.spaceId!, request.workflowId!, request.workflowExecutionId!), request);
  },

  async getNotifications(request?: GetNotificationsRequest): Promise<GetNotificationsResponse> {
    const params = new URLSearchParams();
    if (request?.limit) params.append('limit', request.limit.toString());
    if (request?.olderThan) params.append('olderThan', request.olderThan.toISOString());
    
    const queryString = params.toString();
    const url = queryString ? `${PATH.getNotifications}?${queryString}` : PATH.getNotifications;
    
    const response = await API.get(url);
    
    // Convert date strings to Date objects
    if (response.notifications) {
      response.notifications = response.notifications.map((notification: Notification) => ({
        ...notification,
        createdAt: notification.createdAt ? new Date(notification.createdAt) : notification.createdAt,
        seenAt: notification.seenAt ? new Date(notification.seenAt) : notification.seenAt,
      }));
    }
    
    return response;
  },

  markNotificationsAsSeen(request: MarkNotificationsAsSeenRequest): Promise<void> {
    return API.post(PATH.markNotificationsAsSeen, request);
  },

  getPnls(request: GetPnlsRequest): Promise<GetPnlsResponse> {
    return API.post(PATH.getPnls, request);
  },

  getAgentExecutionFrameSummaries(agentSessionId: string, afterExecutionFrameSummaryId?: string): Promise<GetAgentExecutionFrameSummariesResponse> {
    const frameIdParam = afterExecutionFrameSummaryId ? `?afterExecutionFrameSummaryId=${afterExecutionFrameSummaryId}` : '';
    return API.get(PATH.getAgentExecutionFrameSummaries(agentSessionId) + frameIdParam);
  },
};
